import { getJWT } from '@bynder/js-access-token-lib';
import * as types from 'packages/store/types';
import isInIframe from 'packages/helpers/iframeHelper';
import { loginBynderUser, sendRefreshCommand } from 'packages/store/user/user.actions';
import { envVar } from 'packages/helpers/envVar';

type PlatformConfig = {
    hostUrl?: string;
    tokenEndpoint?: string;
};

export function checkPlatformUsage({ hostUrl, tokenEndpoint }: PlatformConfig = {}): any {
    return (dispatch): any => {
        dispatch({
            type: types.UPDATE_PLATFORM_OPTIONS,
            options: {
                ...(hostUrl ? { hostUrl } : {}),
                ...(tokenEndpoint ? { tokenEndpoint } : {}),
            },
        });

        if (isInIframe()) {
            window.addEventListener('message', function (e) {
                if (e.ports && e.ports.length > 0) {
                    e.ports[0].onmessage = function (e) {
                        dispatch(onMessage(e));
                    };
                    e.ports[0].postMessage({
                        type: 'channel_set',
                    });

                    if (window.Appcues) {
                        e.ports[0].postMessage({
                            type: 'appcues',
                        });
                    }

                    dispatch({
                        type: types.UPDATE_PLATFORM_OPTIONS,
                        options: {
                            hostPort: e.ports[0],
                        },
                    });
                }
            });
        }

        return dispatch(acquireAccessToken());
    };
}

let authRefreshTimer: any = 0;

/**
 * Shared tail of every token acquisition, whichever source produced it.
 *
 * `hostUrl` is only passed by the iframe path, where the host sends its own
 * origin alongside the token; an embedded host supplies it up-front instead.
 */
function applyAccessToken({ token, hostUrl }: { token?: string; hostUrl?: string }): any {
    return (dispatch, getState): void => {
        if (!token) {
            console.error('No access token received from the platform.');

            return;
        }

        if (authRefreshTimer) {
            clearTimeout(authRefreshTimer);
        }

        // `new URL` would throw on the reducer's empty default, which a caller
        // supplying only a token endpoint would otherwise hit.
        const resolvedHostUrl = hostUrl || getState().platform.hostUrl || `${window.location.origin}/`;
        const hostUrlDomain = new URL(resolvedHostUrl).hostname;
        const finalPortalDomain = hostUrlDomain;

        dispatch({
            type: types.UPDATE_PLATFORM_OPTIONS,
            options: {
                bynderAccessToken: token,
                hostUrl: resolvedHostUrl,
                portalDomain: finalPortalDomain,
            },
        });
        dispatch(loginBynderUser());
        authRefreshTimer = setTimeout(() => {
            authRefreshTimer = 0;
            dispatch(refreshAccessToken());
        }, envVar.authRefreshTimeout);
    };
}

export function acquireAccessToken() {
    return async (dispatch, getState): Promise<boolean> => {
        if (!isInIframe()) {
            try {
                const jwt = await getJWT();

                if (jwt) {
                    dispatch(applyAccessToken({ token: jwt }));

                    return true;
                }
            } catch {
                // No host session to read a JWT from — try the endpoint instead.
            }
        }

        const { tokenEndpoint } = getState().platform;

        if (!tokenEndpoint) {
            return false;
        }

        try {
            const response = await fetch(tokenEndpoint);

            if (!response.ok) {
                throw new Error(`${tokenEndpoint} responded ${response.status}`);
            }

            const payload = await response.json();
            dispatch(applyAccessToken({ token: payload.access_token }));

            return true;
        } catch (error) {
            console.error('Could not obtain an access token.', error);
            dispatch({ type: types.TOGGLE_APP_INITIALIZING, state: false });

            return false;
        }
    };
}

/** Re-acquires a token, same order as the first time round. */
export function refreshAccessToken(): any {
    return async (dispatch): Promise<void> => {
        if (await dispatch(acquireAccessToken())) {
            return;
        }

        dispatch(sendRefreshCommand());
    };
}

function onMessage(e: any) {
    return (dispatch): any => {
        // not used right now, but might be useful later
        // if (e.data.type === 'appcues_init') {
        //     AuthorizationHelper.setAppcuesId(e.data.data.id);
        // }
        if (e.data.type === 'access_token') {
            dispatch(applyAccessToken({ token: e.data.data, hostUrl: e.data.hostUrl }));
        }
    };
}
